import {
  Box,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { PageLabelRule } from "generated/graphql";
import { FC, useEffect, useState } from "react";
import { SubmitOptions, useFetcher, useLocation } from "react-router-dom";
import { useFetcherWithSnackbar } from "routes/lens-editor/page-labels/useFetcherWithSnackbar";
import { HelpOutline } from "@mui/icons-material";

interface PageRuleDialogProps {
  open: boolean;
  close: () => void;
  pageLabelRule?: PageLabelRule;
  pageLabelId: number;
  pageLabelRules: PageLabelRule[];
}

const PageLabelRuleDialog: FC<PageRuleDialogProps> = ({ open, close, pageLabelRule, pageLabelId, pageLabelRules }) => {
  const [groupName, setGroupName] = useState<string | undefined>(pageLabelRule?.groupName || undefined);
  const [queryText, setQueryText] = useState<string>(pageLabelRule?.queryText || "");
  const [queryFuzziness, setQueryFuzziness] = useState<string>(pageLabelRule?.queryFuzziness || "-1");
  const [queryPhraseSlop, setQueryPhraseSlop] = useState<number>(pageLabelRule?.queryPhraseSlop || 0);
  const [exclude, setExclude] = useState(pageLabelRule?.exclude || false);
  const canSave = queryText.length > 0;
  const maxQueryPhraseSlop = queryText.split(" ").length;
  const createFetcher = useFetcher();
  const updateFetcher = useFetcher();
  const deleteFetcher = useFetcher();
  const location = useLocation();
  const createdResponse = useFetcherWithSnackbar(
    createFetcher,
    "Page Label Rule created",
    "Unable to create Page Label Rule"
  );
  const updatedResponse = useFetcherWithSnackbar(
    updateFetcher,
    "Page Label Rule updated",
    "Unable to update Page Label Rule"
  );
  const deletedResponse = useFetcherWithSnackbar(
    deleteFetcher,
    "Page Label Rule deleted",
    "Unable to delete Page Label Rule"
  );
  const fetcherSubmitOptions: Partial<SubmitOptions> = {
    encType: "application/json",
    method: "POST",
    action: location.pathname,
  };

  const onSubmit = () => {
    if (!pageLabelRule) {
      createFetcher.submit(
        JSON.stringify({
          pageLabelId,
          groupName: groupName !== "" ? groupName : undefined,
          queryText,
          queryPhraseSlop,
          queryFuzziness: queryFuzziness !== "-1" ? queryFuzziness : undefined,
          exclude,
          intent: "create",
        }),
        fetcherSubmitOptions
      );
    } else {
      updateFetcher.submit(
        JSON.stringify({
          id: pageLabelRule.id,
          pageLabelId,
          groupName: groupName !== "" ? groupName : undefined,
          queryText,
          queryPhraseSlop,
          queryFuzziness: queryFuzziness !== "-1" ? queryFuzziness : undefined,
          exclude,
          intent: "update",
        }),
        fetcherSubmitOptions
      );
    }
  };

  const deleteRule = (ruleId: number) => {
    deleteFetcher.submit(
      {
        id: ruleId,
        pageLabelId,
        queryText,
        intent: "delete",
      },
      fetcherSubmitOptions
    );
  };

  useEffect(() => {
    if (createdResponse.completed || updatedResponse.completed || deletedResponse.completed) {
      close();
    }
  }, [createdResponse, updatedResponse, deletedResponse]);

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>{pageLabelRule ? "Edit" : "New"} Page Label Rule</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            pt: 2,
            gap: 3,
          }}>
          <FormControl fullWidth required>
            <TextField
              value={queryText}
              onChange={(evt) => {
                setQueryText(evt.target.value.toLowerCase());
              }}
              label="Query Text"
            />
          </FormControl>
          <FormControl fullWidth>
            <Autocomplete
              value={groupName || ""}
              size="small"
              disabled={exclude}
              onChange={(_evt, value) => {
                setGroupName(value || "");
              }}
              options={pageLabelRules
                // Let's only get rules with a group name
                .filter((rule) => rule.groupName !== null)
                // Get just the group names
                .map((rule) => rule.groupName)
                // Remove duplicates
                .filter((groupName, index, self) => self.indexOf(groupName) === index)}
              renderInput={(params) => <TextField {...params} label="Group" />}
              freeSolo
              onInputChange={(_evt, value) => {
                setGroupName(value);
              }}
              ListboxProps={{
                style: {
                  whiteSpace: "pre",
                },
              }}
            />
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Query Fuzziness</InputLabel>
            <Select
              label="Query Fuzziness"
              size="small"
              value={queryFuzziness}
              onChange={(evt) => setQueryFuzziness(evt.target.value)}
              displayEmpty>
              <MenuItem value="-1">None</MenuItem>
              <MenuItem value="1">1</MenuItem>
              <MenuItem value="2">2</MenuItem>
              <MenuItem value="3">3</MenuItem>
              <MenuItem value="4">4</MenuItem>
              <MenuItem value="5">5</MenuItem>
              <MenuItem value="AUTO">AUTO</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <TextField
              label="Query Phrase Slop"
              value={queryPhraseSlop}
              onChange={(evt) => setQueryPhraseSlop(parseInt(evt.target.value))}
              type="number"
              helperText="Max value based on number of words in query text"
              inputProps={{
                max: maxQueryPhraseSlop,
              }}
            />
          </FormControl>
        </Box>

        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={exclude}
                onChange={(evt) => {
                  setGroupName(undefined);
                  setExclude(evt.target.checked);
                }}
              />
            }
            label="Exclude Matches"
          />
        </FormControl>
        <Tooltip arrow title="If this rule matches, a page will never have this label. Grouping disabled.">
          <HelpOutline />
        </Tooltip>
      </DialogContent>
      <DialogActions>
        {pageLabelRule && (
          <>
            <Button color="primary" variant="outlined" onClick={deleteRule.bind(this, pageLabelRule.id)}>
              Delete
            </Button>
            <Box sx={{ flexGrow: 1 }} />
          </>
        )}
        <Button color="primary" variant="outlined" onClick={close}>
          Cancel
        </Button>
        <Button onClick={onSubmit} color="primary" variant="contained" disabled={!canSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PageLabelRuleDialog;
